
import React, { useRef, useReducer, useEffect, useCallback } from 'react';
import { Event } from '@/types/event';
import { fetchEvents, toggleEventInterest, toggleEventSave, fetchNearbyEvents, fetchUserSavedEvents, fetchUserInterestedEvents } from '@/api/events';
import SwipeCard from '@/components/SwipeCard/SwipeCard';
import EventDetailPanel from '@/components/EventDetailPanel';
import styles from './SwipeEventsPage.module.css';
// Note: Les icônes sont maintenant utilisées directement dans le CSS ou ont été remplacées par du HTML/CSS natif
import { useAuth } from '@/contexts/AuthContext';

// Déclaration globale pour TypeScript
declare global {
  interface Window {
    __EVENTS_LOADED__?: boolean;
  }
}

// Type pour les coordonnées géographiques
interface Coordinates {
  lat: number;
  lon: number;
}

// Constantes
const VIEWED_EVENTS_KEY = 'swipe_viewed_events';
const VIEWED_EVENTS_TTL = 7 * 24 * 60 * 60 * 1000; // 7 jours
const EVENTS_BATCH_SIZE = 10;
const LOAD_MORE_THRESHOLD = 3;
const VISIBLE_CARDS_LIMIT = 3; // Limite à 3 cartes visibles maximum pour optimiser le rendu

// Définition du type pour l'état du réducteur
interface SwipeState {
  events: Event[];
  allEvents: Event[];
  loadedCount: number;
  currentIndex: number;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  showDetail: boolean;
  showInstructions: boolean;
  hasSeenInstructions: boolean;
  isMobile: boolean;
  viewedEvents: Set<number>;
  showingNewEventsOnly: boolean;
  interestedEvents: Set<number>;
  savedEvents: Set<number>;
  swipingCardId: number | null;
  swipeAnimation: string | null;
  isOutOfEvents: boolean;
  preloadedImages: Map<number, boolean>;
}

// Définition des types d'actions pour le réducteur
type SwipeAction =
  | { type: 'LOAD_EVENTS'; payload: { events: Event[] } }
  | { type: 'SET_ALL_EVENTS'; payload: { events: Event[] } }
  | { type: 'SET_LOADED_COUNT'; payload: { count: number } }
  | { type: 'INCREMENT_CURRENT_INDEX' }
  | { type: 'SET_CURRENT_INDEX'; payload: { index: number } }
  | { type: 'SET_LOADING'; payload: { loading: boolean } }
  | { type: 'SET_LOADING_MORE'; payload: { loadingMore: boolean } }
  | { type: 'SET_ERROR'; payload: { error: string | null } }
  | { type: 'TOGGLE_DETAIL' }
  | { type: 'SET_SHOW_DETAIL'; payload: { show: boolean } }
  | { type: 'TOGGLE_INSTRUCTIONS' }
  | { type: 'SET_SHOW_INSTRUCTIONS'; payload: { show: boolean } }
  | { type: 'SET_HAS_SEEN_INSTRUCTIONS'; payload: { seen: boolean } }
  | { type: 'SET_IS_MOBILE'; payload: { isMobile: boolean } }
  | { type: 'ADD_VIEWED_EVENT'; payload: { eventId: number } }
  | { type: 'RESET_VIEWED_EVENTS' }
  | { type: 'SET_VIEWED_EVENTS'; payload: { eventIds: number[] } }
  | { type: 'TOGGLE_SHOWING_NEW_EVENTS_ONLY' }
  | { type: 'ADD_INTERESTED_EVENT'; payload: { eventId: number } }
  | { type: 'REMOVE_INTERESTED_EVENT'; payload: { eventId: number } }
  | { type: 'SET_INTERESTED_EVENTS'; payload: { eventIds: number[] } }
  | { type: 'ADD_SAVED_EVENT'; payload: { eventId: number } }
  | { type: 'REMOVE_SAVED_EVENT'; payload: { eventId: number } }
  | { type: 'SET_SAVED_EVENTS'; payload: { eventIds: number[] } }
  | { type: 'SET_SWIPING_CARD_ID'; payload: { id: number | null } }
  | { type: 'SET_SWIPE_ANIMATION'; payload: { animation: string | null } }
  | { type: 'CHECK_OUT_OF_EVENTS' }
  | { type: 'MARK_IMAGE_PRELOADED'; payload: { eventId: number } };

// Fonction de réduction pour gérer toutes les mises à jour d'état
const swipeReducer = (state: SwipeState, action: SwipeAction): SwipeState => {
  switch (action.type) {
    case 'LOAD_EVENTS':
      return { ...state, events: action.payload.events };
      
    case 'SET_ALL_EVENTS':
      return { ...state, allEvents: action.payload.events };
      
    case 'SET_LOADED_COUNT':
      return { ...state, loadedCount: action.payload.count };
      
    case 'INCREMENT_CURRENT_INDEX':
      return { ...state, currentIndex: state.currentIndex + 1 };
      
    case 'SET_CURRENT_INDEX':
      return { ...state, currentIndex: action.payload.index };
      
    case 'SET_LOADING':
      return { ...state, loading: action.payload.loading };
      
    case 'SET_LOADING_MORE':
      return { ...state, loadingMore: action.payload.loadingMore };
      
    case 'SET_ERROR':
      return { ...state, error: action.payload.error };
      
    case 'TOGGLE_DETAIL':
      return { ...state, showDetail: !state.showDetail };
      
    case 'SET_SHOW_DETAIL':
      return { ...state, showDetail: action.payload.show };
      
    case 'TOGGLE_INSTRUCTIONS':
      return { ...state, showInstructions: !state.showInstructions };
      
    case 'SET_SHOW_INSTRUCTIONS':
      return { ...state, showInstructions: action.payload.show };
      
    case 'SET_HAS_SEEN_INSTRUCTIONS':
      return { ...state, hasSeenInstructions: action.payload.seen };
      
    case 'SET_IS_MOBILE':
      return { ...state, isMobile: action.payload.isMobile };
      
    case 'ADD_VIEWED_EVENT': {
      const newViewedEvents = new Set(state.viewedEvents);
      newViewedEvents.add(action.payload.eventId);
      return { ...state, viewedEvents: newViewedEvents };
    }
      
    case 'RESET_VIEWED_EVENTS':
      return { ...state, viewedEvents: new Set() };
      
    case 'SET_VIEWED_EVENTS':
      return { ...state, viewedEvents: new Set(action.payload.eventIds) };
      
    case 'TOGGLE_SHOWING_NEW_EVENTS_ONLY':
      return { ...state, showingNewEventsOnly: !state.showingNewEventsOnly };
      
    case 'ADD_INTERESTED_EVENT': {
      const newInterestedEvents = new Set(state.interestedEvents);
      newInterestedEvents.add(action.payload.eventId);
      return { ...state, interestedEvents: newInterestedEvents };
    }
      
    case 'REMOVE_INTERESTED_EVENT': {
      const newInterestedEvents = new Set(state.interestedEvents);
      newInterestedEvents.delete(action.payload.eventId);
      return { ...state, interestedEvents: newInterestedEvents };
    }
      
    case 'SET_INTERESTED_EVENTS':
      return { ...state, interestedEvents: new Set(action.payload.eventIds) };
      
    case 'ADD_SAVED_EVENT': {
      const newSavedEvents = new Set(state.savedEvents);
      newSavedEvents.add(action.payload.eventId);
      return { ...state, savedEvents: newSavedEvents };
    }
      
    case 'REMOVE_SAVED_EVENT': {
      const newSavedEvents = new Set(state.savedEvents);
      newSavedEvents.delete(action.payload.eventId);
      return { ...state, savedEvents: newSavedEvents };
    }
      
    case 'SET_SAVED_EVENTS':
      return { ...state, savedEvents: new Set(action.payload.eventIds) };
      
    case 'SET_SWIPING_CARD_ID':
      return { ...state, swipingCardId: action.payload.id };
      
    case 'SET_SWIPE_ANIMATION':
      return { ...state, swipeAnimation: action.payload.animation };
      
    case 'CHECK_OUT_OF_EVENTS':
      return { 
        ...state, 
        isOutOfEvents: !state.loading && state.events.length > 0 && state.currentIndex >= state.events.length 
      };

    case 'MARK_IMAGE_PRELOADED': {
      const newPreloadedImages = new Map(state.preloadedImages);
      newPreloadedImages.set(action.payload.eventId, true);
      return { ...state, preloadedImages: newPreloadedImages };
    }
      
    default:
      return state;
  }
};

const SwipeEventsPage: React.FC = () => {
  // État initial pour le réducteur
  const initialState: SwipeState = {
    events: [],
    allEvents: [],
    loadedCount: 0,
    currentIndex: 0,
    loading: true,
    loadingMore: false,
    error: null,
    showDetail: false,
    showInstructions: false,
    hasSeenInstructions: false,
    isMobile: false,
    viewedEvents: new Set<number>(),
    showingNewEventsOnly: true,
    interestedEvents: new Set<number>(),
    savedEvents: new Set<number>(),
    swipingCardId: null,
    swipeAnimation: null,
    isOutOfEvents: false,
    preloadedImages: new Map<number, boolean>(),
  };

  // Initialiser le réducteur avec l'état initial
  const [state, dispatch] = useReducer(swipeReducer, initialState);
  
  // Récupérer l'authentification et la navigation
  const authContext = useAuth();
  // const navigate = useNavigate(); // Utilisé pour la navigation conditionnelle en mode non-mobile
  const user = authContext?.currentUser;
  
  // Références pour le suivi du chargement et de la géolocalisation
  const hasLoadedInitialBatch = useRef<boolean>(false);
  const userCoordinates = useRef<Coordinates | null>(null);
  
  // Carte pour suivre les événements en cours de traitement et les timestamps des appels API
  const isEventBeingProcessed = new Map<number, boolean>();
  const lastApiCallTimestamps = useRef(new Map<number, number>());

  // Fonction pour précharger les images des événements à venir
  const preloadEventImages = useCallback((events: Event[]) => {
    const imagesToPreload = events
      .slice(0, VISIBLE_CARDS_LIMIT * 2) // Précharger les prochaines images (2x le nombre visible)
      .filter(event => event.imageUrl && !state.preloadedImages.has(event.id));
    
    imagesToPreload.forEach(event => {
      if (event.imageUrl) {
        const img = new Image();
        img.src = event.imageUrl;
        img.onload = () => {
          dispatch({ type: 'MARK_IMAGE_PRELOADED', payload: { eventId: event.id } });
        };
      }
    });
  }, [state.preloadedImages, dispatch]);

  // Fonction pour filtrer les événements selon les préférences utilisateur
  const filterEvents = useCallback((eventsList: Event[]) => {
    // Créer des identifiants uniques pour chaque événement pour éviter les doublons
    const uniqueEventIds = new Set<number>();
    
    // Filtrer les événements
    const filtered = eventsList.filter(event => {
      const isViewed = state.viewedEvents.has(event.id);
      
      if (uniqueEventIds.has(event.id)) {
        return false; // Éliminer les doublons
      }
      
      uniqueEventIds.add(event.id);
      
      if (state.showingNewEventsOnly && isViewed) {
        return false; // Ignorer les événements déjà vus en mode "nouveaux uniquement"
      }
      
      return true;
    }).map(event => ({
      ...event,
      isInterested: state.interestedEvents.has(event.id),
      isSaved: state.savedEvents.has(event.id)
    }));
    
    return filtered;
  }, [state.viewedEvents, state.interestedEvents, state.savedEvents, state.showingNewEventsOnly]);

  // Initialisation des événements
  const initializeEvents = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: { loading: true } });
      dispatch({ type: 'SET_ERROR', payload: { error: null } });
      
      // Récupération des événements depuis l'API
      const response = await fetchEvents();
      
      // Randomiser l'ordre des événements pour plus de diversité
      const shuffledEvents = [...response].sort(() => Math.random() - 0.5);
      
      dispatch({ type: 'SET_ALL_EVENTS', payload: { events: shuffledEvents } });
      
      // Ne garder que les événements qui ne sont pas dans les vus/intéressés/sauvegardés
      const filteredEvents = filterEvents(shuffledEvents);
      dispatch({ type: 'LOAD_EVENTS', payload: { events: filteredEvents } });
      dispatch({ type: 'SET_LOADED_COUNT', payload: { count: filteredEvents.length } });
      
      // Précharger les images des événements visibles
      preloadEventImages(filteredEvents);
      
      // Réinitialiser l'index courant
      dispatch({ type: 'SET_CURRENT_INDEX', payload: { index: 0 } });
    } catch (err) {
      console.error('Erreur lors du chargement des événements:', err);
      dispatch({ type: 'SET_ERROR', payload: { error: 'Impossible de charger les événements. Veuillez réessayer plus tard.' } });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { loading: false } });
    }
  }, [filterEvents, preloadEventImages, dispatch]);

  // Chargement des événements basé sur la localisation
  // Utilisé seulement une fois au démarrage initial
  const loadEventsBasedOnLocation = useCallback(async (coords: Coordinates) => {
    // Vérifier si les événements ont déjà été chargés
    if (hasLoadedInitialBatch.current) {
      console.log("Les événements ont déjà été chargés, ignorer l'appel");
      return;
    }

    // Définir immediatement que les événements sont en cours de chargement
    // pour éviter les appels multiples
    hasLoadedInitialBatch.current = true;
    
    try {
      dispatch({ type: 'SET_LOADING_MORE', payload: { loadingMore: true } });
      dispatch({ type: 'SET_LOADING', payload: { loading: true } });
      
      console.log("Chargement des événements à proximité (appel unique)...");
      const nearbyEvents = await fetchNearbyEvents(coords);
      
      // Trier par date
      nearbyEvents.sort((a: Event, b: Event) => {
        const dateA = new Date(a.dateTime);
        const dateB = new Date(b.dateTime);
        return dateA.getTime() - dateB.getTime();
      });
      
      // Filtrer selon les préférences utilisateur
      const filteredEvents = filterEvents(nearbyEvents);
      
      dispatch({ type: 'LOAD_EVENTS', payload: { events: filteredEvents } });
      dispatch({ type: 'SET_ALL_EVENTS', payload: { events: filteredEvents } });
      dispatch({ type: 'SET_LOADED_COUNT', payload: { count: filteredEvents.length } });
    } catch (err) {
      console.error('Error loading events by location:', err);
      dispatch({ type: 'SET_ERROR', payload: { error: 'Impossible de charger les événements à proximité.' } });
      // En cas d'erreur, réinitialiser pour permettre une nouvelle tentative
      hasLoadedInitialBatch.current = false;
    } finally {
      dispatch({ type: 'SET_LOADING_MORE', payload: { loadingMore: false } });
      dispatch({ type: 'SET_LOADING', payload: { loading: false } });
    }
  }, [filterEvents, dispatch]);

  // Fonction pour charger les événements initiaux depuis les événements déjà disponibles
  // Actuellement inutilisée mais gardée pour une utilisation future potentielle
  /* 
  const loadInitialEvents = useCallback(() => {
    if (state.allEvents.length === 0 || !hasLoadedInitialBatch.current) return;
    
    const filteredEvents = filterEvents(state.allEvents);
    dispatch({ type: 'LOAD_EVENTS', payload: { events: filteredEvents } });
    dispatch({ type: 'SET_LOADED_COUNT', payload: { count: filteredEvents.length } });
  }, [state.allEvents, filterEvents, dispatch]);
  */

  // Chargement de plus d'événements
  const loadMoreEvents = useCallback(() => {
    if (state.loadedCount >= state.allEvents.length) return;
    
    dispatch({ type: 'SET_LOADING_MORE', payload: { loadingMore: true } });
    
    const nextBatch = state.allEvents.slice(state.loadedCount, state.loadedCount + EVENTS_BATCH_SIZE);
    dispatch({ 
      type: 'LOAD_EVENTS', 
      payload: { events: [...state.events, ...nextBatch] } 
    });
    dispatch({ 
      type: 'SET_LOADED_COUNT', 
      payload: { count: state.loadedCount + nextBatch.length } 
    });
    dispatch({ type: 'SET_LOADING_MORE', payload: { loadingMore: false } });
  }, [state.loadedCount, state.allEvents, state.events, dispatch]);

  // Chargement des préférences utilisateur
  const loadUserPreferences = useCallback(async () => {
    if (user && authContext?.isLoggedIn) {
      try {
        const interested = await fetchUserInterestedEvents();
        dispatch({ 
          type: 'SET_INTERESTED_EVENTS', 
          payload: { eventIds: interested.map((event: Event) => event.id) } 
        });
        const saved = await fetchUserSavedEvents();
        dispatch({ 
          type: 'SET_SAVED_EVENTS', 
          payload: { eventIds: saved.map((event: Event) => event.id) } 
        });
      } catch (err) {
        console.error('Erreur lors du chargement des préférences utilisateur:', err);
      }
    }
  }, [user, authContext, dispatch]);

  // Gestionnaire pour marquer un événement comme intéressant ou non
  const handleToggleInterest = useCallback(async (eventId: number) => {
    const now = Date.now();
    const lastCallTime = lastApiCallTimestamps.current.get(eventId) || 0;
    
    // Éviter les appels trop fréquents pour le même événement
    if (now - lastCallTime < 1000) return;
    
    // Vérifier si l'événement est déjà en cours de traitement
    if (isEventBeingProcessed.get(eventId)) return;
    
    isEventBeingProcessed.set(eventId, true);
    lastApiCallTimestamps.current.set(eventId, now);
    
    const isAlreadyInterested = state.interestedEvents.has(eventId);
    
    try {
      // Optimistic update
      dispatch({ 
        type: isAlreadyInterested ? 'REMOVE_INTERESTED_EVENT' : 'ADD_INTERESTED_EVENT', 
        payload: { eventId } 
      });
      
      // API Call
      await toggleEventInterest(eventId);
    } catch (err) {
      console.error('Erreur lors de la mise à jour de l\'intérêt:', err);
      
      // Rollback en cas d'erreur
      dispatch({ 
        type: isAlreadyInterested ? 'ADD_INTERESTED_EVENT' : 'REMOVE_INTERESTED_EVENT', 
        payload: { eventId } 
      });
    } finally {
      isEventBeingProcessed.set(eventId, false);
    }
  }, [state.interestedEvents, dispatch]);

  // Gestionnaire pour sauvegarder ou désauvegarder un événement
  const handleToggleSave = useCallback(async (eventId: number) => {
    const now = Date.now();
    const lastCallTime = lastApiCallTimestamps.current.get(eventId) || 0;
    
    // Éviter les appels trop fréquents pour le même événement
    if (now - lastCallTime < 1000) return;
    
    // Vérifier si l'événement est déjà en cours de traitement
    if (isEventBeingProcessed.get(eventId)) return;
    
    isEventBeingProcessed.set(eventId, true);
    lastApiCallTimestamps.current.set(eventId, now);
    
    const isAlreadySaved = state.savedEvents.has(eventId);
    
    try {
      // Optimistic update
      dispatch({ 
        type: isAlreadySaved ? 'REMOVE_SAVED_EVENT' : 'ADD_SAVED_EVENT', 
        payload: { eventId } 
      });
      
      // API Call
      await toggleEventSave(eventId);
    } catch (err) {
      console.error('Erreur lors de la mise à jour de la sauvegarde:', err);
      
      // Rollback en cas d'erreur
      dispatch({ 
        type: isAlreadySaved ? 'ADD_SAVED_EVENT' : 'REMOVE_SAVED_EVENT', 
        payload: { eventId } 
      });
    } finally {
      isEventBeingProcessed.set(eventId, false);
    }
  }, [state.savedEvents, dispatch]);

  // Gestionnaire pour l'affichage ou non des instructions
  const handleInstructionsClose = useCallback(() => {
    localStorage.setItem('hasSeenSwipeInstructions', 'true');
    dispatch({ type: 'SET_HAS_SEEN_INSTRUCTIONS', payload: { seen: true } });
    dispatch({ type: 'SET_SHOW_INSTRUCTIONS', payload: { show: false } });
  }, [dispatch]);

// Gestionnaire pour le swipe sur un événement
const handleSwipe = useCallback((eventId: number, direction: string) => {
  // Éviter les swipes sur le même événement à moins d'une seconde d'intervalle
  const now = Date.now();
  const lastCallTime = lastApiCallTimestamps.current.get(eventId) || 0;
  
  if (now - lastCallTime < 1000) {
    console.log(`Swipe ignoré pour l'événement ${eventId} - trop rapide`);
    return;
  }
  
  lastApiCallTimestamps.current.set(eventId, now);
  
  // Marquer l'événement comme vu
  dispatch({ type: 'ADD_VIEWED_EVENT', payload: { eventId } });
    
    // Mettre à jour l'état d'intérêt selon la direction du swipe
    if (direction === 'right') {
      handleToggleInterest(eventId);
    }
    
    // Mettre à jour l'animation de swipe
    dispatch({ 
      type: 'SET_SWIPE_ANIMATION', 
      payload: { animation: direction === 'right' ? 'right' : 'left' } 
    });
    
    // Planifier la réinitialisation de l'animation après qu'elle se soit terminée
    setTimeout(() => {
      dispatch({ type: 'SET_SWIPE_ANIMATION', payload: { animation: null } });
    }, 300);
    
    // Charger plus d'événements si nécessaire
    if (state.currentIndex >= state.events.length - LOAD_MORE_THRESHOLD) {
      loadMoreEvents();
    }
    
    // Incrémenter l'index courant
    dispatch({ type: 'SET_CURRENT_INDEX', payload: { index: state.currentIndex + 1 } });
  }, [state.currentIndex, state.events.length, dispatch, handleToggleInterest, loadMoreEvents]);
  
  // Gestionnaire pour l'affichage des détails d'un événement
  const handleShowDetail = useCallback((eventId: number) => {
    // Marquer l'événement comme actuellement affiché
    console.log(`Affichage des détails pour l'événement ${eventId}`);
    dispatch({ type: 'SET_SHOW_DETAIL', payload: { show: true } });
  }, [dispatch]);
  
  // Gestionnaire pour fermer les détails d'un événement
  const handleCloseDetail = useCallback(() => {
    // Masquer les détails
    dispatch({ type: 'SET_SHOW_DETAIL', payload: { show: false } });
  }, [dispatch]);
  
  // Controller les effets initialisés pour éviter les boucles infinies
  const initialEffectsRun = useRef({
    preferences: false,
    instructions: false,
    mobile: false,
    viewedEvents: false
  });

  // Hook useEffect uniquement pour charger les préférences utilisateur
  useEffect(() => {
    if (initialEffectsRun.current.preferences) return;
    initialEffectsRun.current.preferences = true;
    
    if (authContext?.isLoggedIn) {
      console.log("Chargement des préférences utilisateur");
      loadUserPreferences();
    }
  }, [authContext, loadUserPreferences]);
  
  // Hook useEffect pour initialiser les instructions utilisateur
  useEffect(() => {
    if (initialEffectsRun.current.instructions) return;
    initialEffectsRun.current.instructions = true;
    
    console.log("Vérification des instructions vues");
    // Vérifier si l'utilisateur a déjà vu les instructions
    const hasSeenInstructions = localStorage.getItem('hasSeenSwipeInstructions') === 'true';
    
    // Utiliser setTimeout pour éviter les mises à jour pendant le rendu
    setTimeout(() => {
      dispatch({ type: 'SET_HAS_SEEN_INSTRUCTIONS', payload: { seen: hasSeenInstructions } });
      
      if (!hasSeenInstructions) {
        dispatch({ type: 'SET_SHOW_INSTRUCTIONS', payload: { show: true } });
      }
    }, 0);
  }, [dispatch]);
  
  // Hook useEffect pour la détection mobile
  useEffect(() => {
    if (initialEffectsRun.current.mobile) return;
    initialEffectsRun.current.mobile = true;
    
    console.log("Détection du mode mobile");
    // Détecter si l'appareil est mobile
    const isMobile = window.innerWidth <= 768;
    setTimeout(() => {
      dispatch({ type: 'SET_IS_MOBILE', payload: { isMobile } });
    }, 0);
  }, [dispatch]);
  
  // Hook useEffect pour récupérer les événements vus du localStorage
  useEffect(() => {
    if (initialEffectsRun.current.viewedEvents) return;
    initialEffectsRun.current.viewedEvents = true;
    
    console.log("Récupération des événements vus du localStorage");
    const viewedEventsData = localStorage.getItem(VIEWED_EVENTS_KEY);
    if (viewedEventsData) {
      try {
        const { events, timestamp } = JSON.parse(viewedEventsData);
        const now = Date.now();
        
        if (now - timestamp < VIEWED_EVENTS_TTL) {
          setTimeout(() => {
            dispatch({ type: 'SET_VIEWED_EVENTS', payload: { eventIds: events } });
          }, 0);
        } else {
          localStorage.removeItem(VIEWED_EVENTS_KEY);
        }
      } catch (err) {
        console.error('Erreur lors de la récupération des événements vus:', err);
        localStorage.removeItem(VIEWED_EVENTS_KEY);
      }
    }
  }, [dispatch]);
  
  // Variable pour s'assurer que l'effet de géolocalisation n'est exécuté qu'une seule fois
  const geoEffectRun = useRef(false);

  // Hook useEffect pour géolocaliser l'utilisateur et charger les événements à proximité - UNE SEULE FOIS
  useEffect(() => {
    // Vérifier si l'effet a déjà été exécuté - sécurité supplémentaire contre les boucles infinies
    if (geoEffectRun.current) {
      console.log("Effet géo déjà exécuté, ignorer");
      return;
    }
    geoEffectRun.current = true;
    
    // Vérifier si la géolocalisation a déjà été effectuée (entre sessions)
    if (localStorage.getItem('__EVENTS_LOADED_TIMESTAMP__')) {
      console.log("Géolocalisation déjà effectuée dans une session précédente");
      return;
    }
    
    // Marquer comme chargé dans localStorage pour les prochaines sessions
    localStorage.setItem('__EVENTS_LOADED_TIMESTAMP__', Date.now().toString());
    
    console.log("Premier chargement des événements avec géolocalisation");
    
    // Décalage des appels à l'API pour éviter les cycles de rendu
    let loadingDispatchDone = false;
    const loadingTimer = setTimeout(() => {
      loadingDispatchDone = true;
      dispatch({ type: 'SET_LOADING', payload: { loading: true } });
    }, 100);
    
    // Fonction de géolocalisation
    if (navigator.geolocation) {
      console.log("Tentative de géolocalisation...");
      navigator.geolocation.getCurrentPosition(
        (position) => {
          console.log("Géolocalisation réussie");
          const coords: Coordinates = {
            lat: position.coords.latitude,
            lon: position.coords.longitude
          };
          userCoordinates.current = coords;
          
          // S'assurer que le dispatch de chargement est fait
          if (!loadingDispatchDone) {
            clearTimeout(loadingTimer);
            dispatch({ type: 'SET_LOADING', payload: { loading: true } });
          }
          
          // Charger les événements à proximité
          setTimeout(() => {
            loadEventsBasedOnLocation(coords);
          }, 150);
        },
        (error) => {
          console.error('Erreur de géolocalisation:', error);
          
          // S'assurer que le dispatch de chargement est fait
          if (!loadingDispatchDone) {
            clearTimeout(loadingTimer);
            dispatch({ type: 'SET_LOADING', payload: { loading: true } });
          }
          
          // Utiliser les événements globaux en cas d'échec
          setTimeout(() => {
            initializeEvents();
          }, 150);
        },
        // Options de géolocalisation avec timeout
        { timeout: 10000, maximumAge: 600000 }
      );
    } else {
      console.log('Géolocalisation non supportée');
      
      // S'assurer que le dispatch de chargement est fait
      if (!loadingDispatchDone) {
        clearTimeout(loadingTimer);
        dispatch({ type: 'SET_LOADING', payload: { loading: true } });
      }
      
      // Charger tous les événements
      setTimeout(() => {
        initializeEvents();
      }, 150);
    }
    
    return () => {
      clearTimeout(loadingTimer);
    };
  }, []); // Dépendance vide car nous ne voulons l'exécuter qu'une seule fois
  
  // Hook useEffect pour sauvegarder les événements vus dans le localStorage
  useEffect(() => {
    if (state.viewedEvents.size > 0) {
      localStorage.setItem(
        VIEWED_EVENTS_KEY,
        JSON.stringify({
          events: Array.from(state.viewedEvents),
          timestamp: Date.now()
        })
      );
    }
  }, [state.viewedEvents]);

  // Rendu du composant
  return (
    <div className={styles.container}>
      {state.loading ? (
        <div className={styles.loaderContainer}>
          <div className={styles.loader}></div>
          <p>Chargement des événements...</p>
        </div>
      ) : state.error ? (
        <div className={styles.errorContainer}>
          <p>{state.error}</p>
          <button 
            className={styles.retryButton}
            onClick={() => initializeEvents()}
          >
            Réessayer
          </button>
        </div>
      ) : state.events.length === 0 ? (
        <div className={styles.noEventsContainer}>
          <p>Aucun événement disponible pour le moment.</p>
          <button 
            className={styles.refreshButton}
            onClick={() => initializeEvents()}
          >
            Rafraîchir
          </button>
        </div>
      ) : (
        <div className={styles.cardsContainer}>
          {/* Instructions pour le swipe */}
          {state.showInstructions && (
            <div className={styles.instructionsOverlay}>
              <div className={styles.instructionsContent}>
                <h3>Comment ça marche ?</h3>
                <p>Swipe à droite pour marquer votre intérêt pour un événement.</p>
                <p>Swipe à gauche pour passer à l'événement suivant.</p>
                <p>Appuyez sur la carte pour voir plus de détails.</p>
                <button 
                  className={styles.instructionsButton}
                  onClick={handleInstructionsClose}
                >
                  Compris !    
                </button>
              </div>
            </div>
          )}
          
          {/* Cartes des événements */}
          {state.events
            .slice(state.currentIndex, state.currentIndex + VISIBLE_CARDS_LIMIT)
            .map((event, index) => (
              <SwipeCard
                key={event.id}
                event={event}
                onSwipe={(direction) => handleSwipe(event.id, direction)}
                onInfoClick={() => handleShowDetail(event.id)}
                zIndex={VISIBLE_CARDS_LIMIT - index}
                isTop={index === 0}
                className={index === 0 && state.swipeAnimation ? `swiping-${state.swipeAnimation}` : ''}
              />
            ))
          }
          
          {/* Panneau de détails de l'événement */}
          {state.showDetail && state.events[state.currentIndex] && (
            <EventDetailPanel
              event={state.events[state.currentIndex]}
              onClose={handleCloseDetail}
              onInterestClick={() => handleToggleInterest(state.events[state.currentIndex].id)}
              onSaveClick={() => handleToggleSave(state.events[state.currentIndex].id)}
              userInterested={state.events[state.currentIndex].isInterested}
              userSaved={state.events[state.currentIndex].isSaved}
              userLocation={userCoordinates.current}
            />
          )}
          
          {/* Indicateur de chargement pour les événements supplémentaires */}
          {state.loadingMore && (
            <div className={styles.moreLoader}>
              <div className={styles.loader}></div>
              <p>Chargement de plus d'événements...</p>
            </div>
          )}
          
          {/* Message quand tous les événements ont été vus */}
          {state.isOutOfEvents && (
            <div className={styles.outOfEventsMessage}>
              <p>Vous avez vu tous les événements disponibles !</p>
              <button 
                className={styles.refreshButton}
                onClick={() => initializeEvents()}
              >
                Rafraîchir
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SwipeEventsPage;
